import {
  Calendar,
  Crown,
  Edit3,
  Flag,
  Heart,
  MapPin,
  MessageCircle,
  Share,
} from "lucide-react";
import { UserRating } from "../UserRating";
import { Button } from "../ui/button";
import { Link } from "react-router-dom";
import { Badge } from "../ui/badge";
import { useAuth } from "../../contexts/AuthContext";
import { useChat } from "../../contexts/ChatContext";
import { ReportModal } from "../ReportModal";
import { useEffect, useState } from "react";
import { saveListing, unsaveListing } from "../../api/listingApi";

export const ListingContent = ({ listing }) => {
  const { currentUser } = useAuth();
  const { setSelectedUserWithListing } = useChat();
  const [showReportModal, setShowReportModal] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  console.log("listing content", listing);

  useEffect(() => {
    if (!currentUser || !listing) return;

    const savedIds = currentUser.savedListings?.map((id) => id.toString());
    const listingId = listing.id?.toString() || listing._id?.toString();

    if (savedIds?.includes(listingId)) {
      setIsSaved(true);
    }
  }, [currentUser, listing]);

  // Add null check to prevent error when listing is not loaded yet
  if (!listing) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  const isCurrentUserListing = currentUser?._id === listing.sellerId;

  // Helper function to format location string
  const getLocationString = () => {
    if (!listing.location) return "Location not specified";
    
    // If location is populated correctly: location is suburb object with nested city, province, country
    if (listing.location.name && listing.location.city?.name) {
      const parts = [
        listing.location.name, // Suburb
        listing.location.city.name, // City
        listing.location.city.province?.name, // Province
        listing.location.city.province?.country?.name // Country
      ].filter(Boolean); // Remove any undefined/null values
      
      return parts.join(", ");
    }
    
    // Fallback if location is just a string (ID)
    if (typeof listing.location === 'string') {
      return "Location ID: " + listing.location.substring(0, 8) + "...";
    }
    
    return "Location not available";
  };

  // Handle contact button click
  const handleContactClick = () => {
    const userToContact = {
      _id: listing.sellerId,
      id: listing.sellerId,
      name: listing.userName,
      avatar: listing.userAvatar,
    };

    const listingInfo = {
      id: listing.id,
      title: listing.title,
      category: listing.category,
      location: listing.location,
    };

    setSelectedUserWithListing(userToContact, listingInfo);
  };

  const handleSaveClick = async () => {
    if (!currentUser) {
      setSaveMessage("Please log in to save listings.");
      return;
    }

    if (isCurrentUserListing) {
      setSaveMessage("You cannot save your own listing.");
      return;
    }

    setSaving(true);
    setSaveMessage(""); // clear previous

    const token = localStorage.getItem("token");
    const listingId = listing.id || listing._id;

    const response = isSaved
      ? await unsaveListing(listingId, token)
      : await saveListing(listingId, token);

    if (response.success) {
      setIsSaved(!isSaved); // toggle saved state
      setSaveMessage(
        isSaved ? "Listing removed from saved." : "Listing saved!"
      );
    } else {
      setSaveMessage(response.message || "Something went wrong.");
    }

    // Clear the message after 3 seconds
    setTimeout(() => {
      setSaveMessage("");
    }, 3000);

    setSaving(false);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-fade-in">
      {/* Image Section */}
      <div className="md:order-2">
        <div className="relative rounded-xl overflow-hidden shadow-md">
          <img
            src={listing.imageUrl}
            alt={listing.title}
            className="w-full h-full object-cover aspect-square"
          />

          <div className="absolute top-3 right-3 text-white px-2 py-1 rounded-full text-xs font-medium">
            <Badge>{listing.category}</Badge>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="md:order-1">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl font-bold mb-4">{listing.title}</h1>
          <div className="flex items-center space-x-2">
            {saveMessage && (
              <p
                className={`text-sm mt-1 ${
                  isSaved ? "text-green-600" : "text-red-600"
                }`}
              >
                {saveMessage}
              </p>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleSaveClick}
              disabled={saving}
              className={isSaved ? "text-red-500" : ""}
              title={isSaved ? "Unsave listing" : "Save listing"}
            >
              <Heart fill={isSaved ? "currentColor" : "none"} />
            </Button>

            {currentUser?._id === listing.sellerId && (
              <Link to={`/listing/${listing.id}/edit`}>
                <Button variant="outline" size="sm" className="gap-1">
                  <Edit3 className="h-4 w-4" />
                  Edit
                </Button>
              </Link>
            )}
            {/* Report Button - only show if not current user's listing */}
            {!isCurrentUserListing && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowReportModal(true)}
                className="text-gray-400 hover:text-red-500 hover:bg-red-50"
                title="Report listing"
              >
                <Flag className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-4 mb-4">
          <div className="flex items-center text-muted-foreground">
            <MapPin className="h-4 w-4 mr-2" />
            <span className="text-sm">{getLocationString()}</span>
          </div>
          <div className="flex items-center text-muted-foreground">
            <Calendar className="h-4 w-4 mr-2" />
            {listing.date}
          </div>
        </div>

        <p className="text-muted-foreground mb-6">{listing.description}</p>

        {/* BarterFor Section */}
        <div className="mb-6 p-4 bg-emerald-50 rounded-lg border border-emerald-100">
          <div className="flex items-center mb-3">
            <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center mr-3">
              <span className="text-white text-sm">⇄</span>
            </div>
            <h3 className="font-semibold text-emerald-800">
              Looking to trade for:
            </h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {listing.barterFor.map((item, index) => (
              <Badge
                key={index}
                variant="secondary"
                className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
              >
                {item}
              </Badge>
            ))}
          </div>
        </div>

        {/* User section */}
        <div className="border-t border-border/60 pt-6">
          <div className="flex items-center justify-between">
            <Link
              to={
                currentUser?._id === listing.sellerId
                  ? "/profile"
                  : `/profile/${listing.sellerId}`
              }
              className="flex items-center space-x-3 hover:bg-gray-50 p-2 rounded-lg transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center overflow-hidden">
                {listing.userAvatar ? (
                  <img
                    src={listing.userAvatar}
                    alt={listing.userName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-sm font-medium text-emerald-700">
                    {listing.userName?.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div>
                <h4 className="font-medium">{listing.userName}</h4>
                <UserRating rating={listing.userRating || 0} showRatingText />
              </div>
              {listing.isFoundingMember && (
                <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-sm px-2 py-1 gap-1.5">
                  <Crown className="h-3.5 w-3.5" />
                  Founder
                </Badge>
              )}
            </Link>

            <div className="flex items-center space-x-2">
              {!isCurrentUserListing && (
                <Link to="/messages" onClick={handleContactClick}>
                  <Button variant="outline" className="gap-2">
                    <MessageCircle className="h-4 w-4" />
                    Contact
                  </Button>
                </Link>
              )}
              <Button variant="ghost" className="gap-2">
                <Share className="h-4 w-4" />
                Share
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Report Modal */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        targetId={listing.id}
        targetType="listing"
        targetName={listing.title}
      />
    </div>
  );
};